// @ts-strict-ignore
import React, { useCallback, useEffect, useState } from 'react';
import { Flex } from '../..';
import { ApiPosition } from 'phoenix/redux/models';
import { DescriptionLabel, SymbolLabel } from '../Shared/Labels';
import { SecurityCellChart } from '../Shared/SecurityCellChart';
import { SecurityCellLogo } from '../Shared/SecurityCellLogo';
import { SecurityNameSection } from '../Shared/SecurityNameSection';
import style from '../style.module.scss';
import { PositionValue } from './PositionValue';
import { SecondaryRow } from './SecondaryRow';
import { XS } from 'phoenix/xstream/XS';
import { useXstreamDispatch } from 'phoenix/xstream/XstreamProvider';
import { useInView } from 'react-intersection-observer';
import { GetSecurityQuoteAction } from 'phoenix/redux/actions';
import { useDispatch } from 'react-redux';
import { QualifiedId } from 'phoenix/util/QualifiedId';

export interface EquityPositionCellProps {
    getCached: boolean;
    hideAccountName?: boolean;
    position: ApiPosition;
    streamingNamespace: string;
    symbol: string;
    visible?: boolean;
}

export const EquityPositionCell = React.memo((props: EquityPositionCellProps) => {
    const xdispatch = useXstreamDispatch();
    const dispatch = useDispatch();
    const { streamingNamespace, position, hideAccountName, symbol } = props;

    const { ref, inView } = useInView({ trackVisibility: true, threshold: 0, delay: 400 });
    const [subscription, setSubscription] = useState<string>(null);
    const [displayedSymbol, setDisplayedSymbol] = useState<string>('');
    const [displayedDescription, setDisplayedDescription] = useState<string>('');

    useEffect(() => {
        const specialPrefix = symbol?.startsWith('B:') || symbol?.startsWith('C:');

        if (specialPrefix) {
            setDisplayedSymbol(position?.description?.toUpperCase() || '');
            setDisplayedDescription(QualifiedId.RemovePrefix(symbol) || '');
        } else {
            setDisplayedSymbol(symbol || '');
            setDisplayedDescription(position?.description?.toLowerCase() || '');
        }
    }, [symbol, position]);

    useEffect(() => {
        dispatch(GetSecurityQuoteAction(symbol));
    }, [symbol, inView]);

    useEffect(() => {
        const run = async () => {
            if (!inView) {
                XS.stop(subscription);
                setSubscription(null);
            } else {
                await dispatch(GetSecurityQuoteAction(symbol));

                if (!subscription) {
                    const ticket: string = await xdispatch(XS.Quotes.start(symbol, streamingNamespace));
                    setSubscription(ticket);
                }
            }
        };

        run();
        return () => {
            XS.stop(subscription);
        };
    }, [inView]);

    const [latestChartPercChange, setLatestChartPercChange] = useState(null);
    const [latestChartPrice, setLatestChartPrice] = useState(null);

    const onCrosshairUpdate = useCallback((value?: any) => {
        setLatestChartPercChange(value[symbol]?.chartPercChange);
        setLatestChartPrice(typeof value[symbol] === 'number' ? value[symbol] : value[symbol]?.value);
    }, []);

    return (
        <>
            <SecurityCellLogo symbol={symbol} />
            <Flex ref={ref} column className={style.cellContent}>
                <Flex className={style.mainRow}>
                    <SecurityNameSection>
                        <SymbolLabel tooltipText={displayedSymbol}>{displayedSymbol}</SymbolLabel>
                        <DescriptionLabel>{displayedDescription}</DescriptionLabel>
                    </SecurityNameSection>
                    <SecurityCellChart withQuoteOverride symbol={symbol} onCrosshairUpdate={onCrosshairUpdate} />
                    <PositionValue withQuoteOverride chartPercChange={latestChartPercChange} chartPrice={latestChartPrice} position={position} symbol={symbol} />
                </Flex>
                <SecondaryRow hideAccountName={hideAccountName} position={position} secType='equity' />
            </Flex>
        </>
    );
});
